import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, IndianRupee, TrendingUp, TrendingDown, CheckCircle, Clock,
  AlertTriangle, Ambulance, Stethoscope, Plus, ArrowRight, Calendar,
  Sparkles, ChevronRight,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { usePartnerPortal } from './PartnerPortalContext';
import { cn } from '../../utils/tailwindUtils';


const PRESETS = [
  { key: '7d',  label: '7 days',  days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
];

const fmtINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const isoDaysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const relativeDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7)   return `${diff} days ago`;
  if (diff < 30)  return `${Math.round(diff / 7)}w ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const DOCTOR_STATUS_LABEL = {
  referred: 'Referred', contacted: 'Contacted', appointment_booked: 'Appointment booked',
  consulted: 'Consulted', converted: 'Converted', dropped: 'Dropped',
};
const AMB_STATUS_LABEL = {
  arrived: 'Arrived', admitted: 'Admitted', discharged: 'Discharged', cancelled: 'Cancelled',
};

const DOCTOR_STATUS_TINT = {
  referred:           'bg-blue-500',
  contacted:          'bg-cyan-500',
  appointment_booked: 'bg-teal-500',
  consulted:          'bg-amber-500',
  converted:          'bg-emerald-500',
  dropped:            'bg-rose-500',
};
const AMB_STATUS_TINT = {
  arrived:    'bg-blue-500',
  admitted:   'bg-teal-500',
  discharged: 'bg-emerald-500',
  cancelled:  'bg-rose-500',
};


export default function PartnerPortalDashboard() {
  const { who } = usePartnerPortal();
  const { fetch: apiFetch } = useApiMethods();
  const isAmbulance = who?.kind === 'ambulance';
  const dashEndpoint = isAmbulance ? 'engage/portal/ambulance/dashboard/' : 'engage/portal/dashboard/';
  const listEndpoint = isAmbulance ? 'engage/portal/ambulance/referrals/' : 'engage/portal/referrals/';
  const listPath     = isAmbulance ? '/partner-portal/pickups' : '/partner-portal/referrals';
  const newPath      = isAmbulance ? '/partner-portal/pickups/new' : '/partner-portal/referrals/new';

  const [preset, setPreset] = useState('30d');
  const [data, setData] = useState(null);          // current-window dashboard payload
  const [prev, setPrev] = useState(null);          // previous window (for deltas)
  const [recent, setRecent] = useState([]);        // last N referrals/pickups
  const [loading, setLoading] = useState(true);

  const { range, prevRange } = useMemo(() => {
    const p = PRESETS.find(x => x.key === preset) || PRESETS[1];
    const from = isoDaysAgo(p.days - 1);
    const to   = today();
    const prevFrom = isoDaysAgo(2 * p.days - 1);
    const prevTo   = isoDaysAgo(p.days);
    return { range: { from, to }, prevRange: { from: prevFrom, to: prevTo } };
  }, [preset]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [cur, prv, listResp] = await Promise.all([
          apiFetch(dashEndpoint, range),
          apiFetch(dashEndpoint, prevRange),
          apiFetch(listEndpoint),
        ]);
        if (cancelled) return;
        setData(cur?.data || null);
        setPrev(prv?.data || null);
        setRecent((listResp?.results || []).slice(0, 6));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashEndpoint, listEndpoint, range, prevRange]);

  const kpis = data?.kpis || {};
  const prevKpis = prev?.kpis || {};
  const series = data?.revenue_series || [];
  const funnel = data?.funnel || {};

  const pendingAmount = Number(kpis.revenue_pending || 0);
  const showPendingCallout = !loading && pendingAmount >= 1;

  return (
    <div className="p-5 max-w-[1200px] mx-auto space-y-5">

      {/* Hero — greeting + range picker + primary CTA */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wide font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            {greeting()}
          </div>
          <h1 className="text-[22px] sm:text-2xl font-bold text-slate-900 dark:text-white leading-tight mt-0.5">
            {who?.name || (isAmbulance ? 'Partner' : 'Doctor')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {shortDate(range.from)} — {shortDate(range.to)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-0.5 shadow-sm">
            {PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className={cn(
                  'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                  preset === p.key
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60',
                )}
              >{p.label}</button>
            ))}
          </div>
          <Link
            to={newPath}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm shadow-sm hover:from-emerald-600 hover:to-teal-700"
          >
            <Plus className="w-4 h-4" /> {isAmbulance ? 'Record pickup' : 'Add referral'}
          </Link>
        </div>
      </div>

      {/* Pending payment callout */}
      {showPendingCallout && (
        <Link
          to={listPath}
          className="group flex items-center justify-between gap-3 p-3 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/15 hover:bg-amber-100 dark:hover:bg-amber-900/25 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
              <Clock className="w-4.5 h-4.5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">₹ {fmtINR(pendingAmount)} pending payout</div>
              <div className="text-[11px] text-amber-800/80 dark:text-amber-200/80">
                Across {isAmbulance ? 'pickups awaiting settlement' : 'referrals awaiting settlement'}. Hospital will release these once verified.
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      )}

      {/* KPI cards with period-over-period delta */}
      {isAmbulance ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard icon={Ambulance}       tint="blue"    label="Total pickups"
                   value={kpis.total_pickups} prev={prevKpis.total_pickups} loading={loading} />
          <KpiCard icon={CheckCircle}     tint="green"   label="Admitted"
                   value={kpis.admitted} prev={prevKpis.admitted} loading={loading} />
          <KpiCard icon={AlertTriangle}   tint="rose"    label="Emergencies"
                   value={kpis.emergencies} prev={prevKpis.emergencies} loading={loading} />
          <KpiCard icon={IndianRupee}     tint="amber"   label="Earnings"
                   value={`₹ ${fmtINR(kpis.revenue_total)}`}
                   prev={prevKpis.revenue_total} prevRaw={kpis.revenue_total}
                   sublabel={`Paid ₹ ${fmtINR(kpis.revenue_paid)} · Pending ₹ ${fmtINR(kpis.revenue_pending)}`}
                   loading={loading} />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard icon={Stethoscope} tint="purple" label="Total referrals"
                   value={kpis.total_referrals} prev={prevKpis.total_referrals} loading={loading} />
          <KpiCard icon={CheckCircle} tint="green"  label="Converted"
                   value={kpis.converted} prev={prevKpis.converted} loading={loading} />
          <KpiCard icon={TrendingUp}  tint="blue"   label="Conversion rate"
                   value={`${kpis.conversion_rate ?? 0}%`}
                   prev={prevKpis.conversion_rate} prevRaw={kpis.conversion_rate}
                   isPercent
                   loading={loading} />
          <KpiCard icon={IndianRupee} tint="amber"  label="Earnings"
                   value={`₹ ${fmtINR(kpis.revenue_total)}`}
                   prev={prevKpis.revenue_total} prevRaw={kpis.revenue_total}
                   sublabel={`Paid ₹ ${fmtINR(kpis.revenue_paid)} · Pending ₹ ${fmtINR(kpis.revenue_pending)}`}
                   loading={loading} />
        </div>
      )}

      {/* Chart + Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Incentive trend</h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Daily incentive value. Hover a point to see the exact figure.
              </p>
            </div>
            {series.length > 0 && !loading && (
              <div className="text-right text-[11px] text-slate-500 dark:text-slate-400">
                <div>Total <b className="text-slate-800 dark:text-white">₹ {fmtINR(series.reduce((s, r) => s + Number(r.revenue || 0), 0))}</b></div>
              </div>
            )}
          </div>
          <div className="mt-3">
            <Sparkline series={series} loading={loading} />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Status funnel</h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Where {isAmbulance ? 'pickups' : 'referrals'} stand today.</p>
          <div className="mt-3">
            <FunnelList
              funnel={funnel}
              loading={loading}
              labelMap={isAmbulance ? AMB_STATUS_LABEL : DOCTOR_STATUS_LABEL}
              tintMap={isAmbulance ? AMB_STATUS_TINT : DOCTOR_STATUS_TINT}
            />
          </div>
        </div>
      </div>

      {/* Recent activity + Shortcut cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              Recent {isAmbulance ? 'pickups' : 'referrals'}
            </h3>
            <Link to={listPath} className="text-[11px] text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-medium inline-flex items-center gap-0.5">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <RecentList
            rows={recent}
            loading={loading}
            isAmbulance={isAmbulance}
            statusMap={isAmbulance ? AMB_STATUS_LABEL : DOCTOR_STATUS_LABEL}
            statusTint={isAmbulance ? AMB_STATUS_TINT : DOCTOR_STATUS_TINT}
            emptyLink={newPath}
          />
        </div>

        <div className="space-y-3">
          <ShortcutCard
            to={newPath}
            icon={Plus}
            title={isAmbulance ? 'Record a pickup' : 'Add a referral'}
            subtitle={isAmbulance ? 'Log a patient before or during dispatch.' : 'Send patient details to the hospital.'}
            accent="emerald"
          />
          <ShortcutCard
            to={listPath}
            icon={Users}
            title={isAmbulance ? 'All my pickups' : 'All my referrals'}
            subtitle="Filter by status, search, follow-up on each."
            accent="slate"
          />
        </div>
      </div>
    </div>
  );
}


// ─── KPI Card ────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, tint = 'blue', label, value, sublabel, prev, prevRaw, isPercent, loading }) {
  const tintMap = {
    blue:   'bg-blue-50   text-blue-600   dark:bg-blue-900/20   dark:text-blue-400',
    green:  'bg-green-50  text-green-600  dark:bg-green-900/20  dark:text-green-400',
    amber:  'bg-amber-50  text-amber-600  dark:bg-amber-900/20  dark:text-amber-400',
    rose:   'bg-rose-50   text-rose-600   dark:bg-rose-900/20   dark:text-rose-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
  };

  // Compute delta relative to the previous window
  const rawCurrent = typeof value === 'string'
    ? Number(prevRaw ?? String(value).replace(/[^0-9.-]/g, '')) || 0
    : Number(value || 0);
  const rawPrev = Number(prev || 0);
  let delta = null;
  if (!loading && !isNaN(rawCurrent) && rawPrev > 0) {
    const diff = rawCurrent - rawPrev;
    const pct = (diff / rawPrev) * 100;
    delta = { diff, pct, up: diff > 0, down: diff < 0 };
  } else if (!loading && rawCurrent > 0 && rawPrev === 0) {
    delta = { diff: rawCurrent, pct: null, up: true, first: true };
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-3.5 border border-slate-100 dark:border-slate-700 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">{label}</span>
        <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', tintMap[tint])}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2 min-h-[28px]">
        <div className="text-[22px] font-bold text-slate-900 dark:text-white leading-none">
          {loading ? <span className="inline-block w-16 h-5 bg-slate-100 dark:bg-slate-700 rounded animate-pulse" /> : (value ?? 0)}
        </div>
        {!loading && delta && (
          <div className={cn(
            'inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-md',
            delta.up   ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/25'
            : delta.down ? 'text-rose-700 bg-rose-50 dark:text-rose-300 dark:bg-rose-900/25'
            :              'text-slate-500 bg-slate-100 dark:text-slate-400 dark:bg-slate-700/40'
          )}>
            {delta.up ? <TrendingUp className="w-3 h-3" /> : delta.down ? <TrendingDown className="w-3 h-3" /> : null}
            {delta.first ? 'new' : delta.pct !== null ? `${Math.abs(Math.round(delta.pct))}${isPercent ? 'pp' : '%'}` : ''}
          </div>
        )}
      </div>
      {sublabel && !loading && (
        <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 truncate">{sublabel}</div>
      )}
    </div>
  );
}


// ─── Sparkline chart ─────────────────────────────────────────────────────────
function Sparkline({ series, loading }) {
  const [hover, setHover] = useState(null);

  if (loading) return <div className="h-40 bg-slate-50 dark:bg-slate-900/40 rounded-lg animate-pulse" />;
  if (!series.length) {
    return (
      <div className="h-40 flex flex-col items-center justify-center text-slate-400 text-sm gap-1">
        <TrendingUp className="w-6 h-6 text-slate-300 dark:text-slate-600" />
        No data yet
      </div>
    );
  }

  const W = 640, H = 150, PAD_X = 12, PAD_Y = 12;
  const vals = series.map(s => Number(s.revenue || 0));
  const max = Math.max(1, ...vals);
  const step = series.length > 1 ? (W - PAD_X * 2) / (series.length - 1) : 0;
  const pts = series.map((s, i) => {
    const x = PAD_X + i * step;
    const y = H - PAD_Y - (Number(s.revenue || 0) / max) * (H - PAD_Y * 2);
    return { x, y, s };
  });
  const path = pts.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(' ');
  const area = `${path} L${pts[pts.length - 1].x},${H - PAD_Y} L${pts[0].x},${H - PAD_Y} Z`;

  // Grid lines (4 horizontal)
  const gridLines = [0.25, 0.5, 0.75, 1].map(f => H - PAD_Y - f * (H - PAD_Y * 2));

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none"
           onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="pp-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#10b981" stopOpacity="0.30" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid */}
        {gridLines.map((y, i) => (
          <line key={i} x1={PAD_X} y1={y} x2={W - PAD_X} y2={y}
                stroke="currentColor" strokeWidth="1" strokeDasharray="2,4"
                className="text-slate-200 dark:text-slate-700" />
        ))}
        {/* Area + line */}
        <path d={area} fill="url(#pp-grad)" />
        <path d={path} fill="none" stroke="#10b981" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Invisible hover targets */}
        {pts.map((p, i) => (
          <g key={i}>
            <rect
              x={p.x - (step / 2 || 8)} y={0}
              width={step || 16} height={H}
              fill="transparent"
              onMouseEnter={() => setHover({ ...p, i })}
            />
            <circle cx={p.x} cy={p.y} r={hover?.i === i ? 4 : 2.5}
                    fill={hover?.i === i ? '#059669' : '#10b981'}
                    className="transition-all pointer-events-none" />
          </g>
        ))}
        {/* Hover indicator */}
        {hover && (
          <g pointerEvents="none">
            <line x1={hover.x} y1={PAD_Y} x2={hover.x} y2={H - PAD_Y}
                  stroke="#10b981" strokeWidth="1" strokeDasharray="3,3" />
          </g>
        )}
      </svg>
      {/* Hover tooltip */}
      {hover && (
        <div
          className="absolute -top-1 px-2 py-1 rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-[11px] shadow-lg pointer-events-none whitespace-nowrap"
          style={{ left: `calc(${(hover.x / W) * 100}% - 40px)` }}
        >
          <div className="font-semibold">{shortDate(hover.s.date)}</div>
          <div className="opacity-80">₹ {fmtINR(hover.s.revenue)} · {hover.s.count} {hover.s.count === 1 ? 'entry' : 'entries'}</div>
        </div>
      )}
      {/* Axis labels */}
      <div className="flex items-center justify-between text-[10px] text-slate-400 dark:text-slate-500 mt-1 px-1">
        <span>{shortDate(series[0]?.date)}</span>
        <span>Peak ₹ {fmtINR(max)}</span>
        <span>{shortDate(series[series.length - 1]?.date)}</span>
      </div>
    </div>
  );
}


// ─── Funnel list ─────────────────────────────────────────────────────────────
function FunnelList({ funnel, loading, labelMap, tintMap }) {
  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="h-6 bg-slate-100 dark:bg-slate-700/60 rounded animate-pulse" />
    ))}</div>;
  }
  const rows = Object.entries(labelMap).map(([k, label]) => ({ k, label, count: funnel[k] || 0 }));
  const total = rows.reduce((s, r) => s + r.count, 0);
  const max = Math.max(1, ...rows.map(r => r.count));
  if (!total) {
    return (
      <div className="h-32 flex flex-col items-center justify-center text-slate-400 text-sm gap-1">
        <Users className="w-6 h-6 text-slate-300 dark:text-slate-600" />
        No entries yet
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {rows.map(r => (
        <div key={r.k}>
          <div className="flex justify-between text-[11px] mb-1">
            <span className="text-slate-700 dark:text-slate-300">{r.label}</span>
            <span className="font-semibold text-slate-900 dark:text-white">{r.count}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', tintMap[r.k] || 'bg-slate-400')}
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}


// ─── Recent activity list ────────────────────────────────────────────────────
function RecentList({ rows, loading, isAmbulance, statusMap, statusTint, emptyLink }) {
  if (loading) {
    return (
      <div className="p-3 space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 rounded bg-slate-100 dark:bg-slate-700/60 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="px-4 py-10 flex flex-col items-center text-center gap-2">
        <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center">
          <Users className="w-5 h-5 text-slate-400" />
        </div>
        <div className="text-sm text-slate-500 dark:text-slate-400">
          No {isAmbulance ? 'pickups' : 'referrals'} yet.
        </div>
        <Link to={emptyLink} className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm font-medium hover:text-emerald-700">
          <Plus className="w-4 h-4" /> {isAmbulance ? 'Record your first pickup' : 'Add your first referral'}
        </Link>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
      {rows.map(r => {
        const name = r.patient_name || (r.enquiry ? `Enquiry #${r.enquiry}` : '—');
        const meta = isAmbulance ? (r.condition || r.pickup_location || '') : (r.expected_specialty || r.clinical_reason || '');
        return (
          <li key={r.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
            <div className={cn(
              'w-2 h-2 rounded-full flex-shrink-0',
              statusTint[r.status] || 'bg-slate-400',
            )} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{name}</div>
              {meta && <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{meta}</div>}
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[11px] font-medium text-slate-700 dark:text-slate-200">
                {r.incentive_paid
                  ? <span className="text-emerald-600 dark:text-emerald-400">₹ {fmtINR(r.incentive_amount)}</span>
                  : Number(r.incentive_amount) > 0
                    ? <span>₹ {fmtINR(r.incentive_amount)}</span>
                    : <span className="text-slate-400">—</span>}
              </div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500">
                {statusMap[r.status] || r.status} · {relativeDay(r.referral_date)}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}


// ─── Shortcut card ───────────────────────────────────────────────────────────
function ShortcutCard({ to, icon: Icon, title, subtitle, accent = 'emerald' }) {
  const accentMap = {
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
    slate:   'bg-slate-100  dark:bg-slate-700/50 text-slate-600 dark:text-slate-300',
  };
  return (
    <Link
      to={to}
      className="group bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-3.5 shadow-sm flex items-center gap-3 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors"
    >
      <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', accentMap[accent])}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 dark:text-white">{title}</div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{subtitle}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
    </Link>
  );
}
